import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthenticationService } from 'app/auth/service';
import { DeviceFingerprintService } from 'app/auth/service/device-fingerprint.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _deviceFingerprintService: DeviceFingerprintService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(err => {
        if (err.status === 401 && !this.isAuthEndpoint(request)) {
          return from(this._authenticationService.refreshToken()).pipe(
            switchMap((newToken: string) => {
              // JwtInterceptor already cloned the OLD bearer token onto `request`
              // before the network call. We must rebuild the request with the
              // fresh token before retrying, or the retry hits another 401.
              return next.handle(this.cloneWithFreshAuth(request, newToken));
            }),
            catchError((refreshError) => {
              // Only force logout for real auth failures from Firebase, not for
              // transient network/server errors during refresh.
              if (this.isAuthFailure(refreshError)) {
                this._authenticationService.logout();
                this._router.navigate(['/pages/authentication/login-v2']);
              }
              return throwError(refreshError);
            })
          );
        }

        if(err.status === 400) {
          return throwError(err.error ?? err);
        }

        if (err.status === 403) {
          this._router.navigate(['/pages/miscellaneous/not-authorized']);
        }

        const error = (err.error && err.error.message) || err.statusText;
        return throwError(error);
      })
    );
  }

  private isAuthEndpoint(request: HttpRequest<any>): boolean {
    return request.url.includes('cloudfunctions') || request.url.includes('/login');
  }

  private cloneWithFreshAuth(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const user = this._authenticationService.currentUserValue;
    const setHeaders: { [key: string]: string } = {
      Authorization: `Bearer ${token}`,
    };
    if (user) {
      if (user.tenantId) setHeaders.Client = user.tenantId;
      if (user.customerId) setHeaders.CustomerId = user.customerId;
    }
    const deviceId = this._deviceFingerprintService.getStoredDeviceId();
    if (deviceId) setHeaders['X-Device-ID'] = deviceId;
    return request.clone({ setHeaders });
  }

  private isAuthFailure(error: any): boolean {
    if (!error) return false;
    // Only force logout for TERMINAL auth failures. Transient problems —
    // network blips (auth/network-request-failed), the SDK not yet rehydrated
    // after wake ("Firebase user not available"), or a momentarily missing
    // local session — must NOT log the user out; the next request or a wake
    // refresh recovers them. This was previously `code.startsWith('auth/')`,
    // which incorrectly treated transient errors as terminal.
    const code: string = (error && error.code) || '';
    const terminal = [
      'auth/user-disabled',
      'auth/user-token-expired',
      'auth/user-token-revoked',
      'auth/invalid-user-token',
      'auth/user-not-found',
    ];
    return typeof code === 'string' && terminal.includes(code);
  }
}
